"use client";

import { useEffect, useLayoutEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "next/navigation";
import { useLocation } from "react-router-dom";
import { getMyProfile } from "@/actions/profiles";
import ProfileContent from "./ProfileContent";
import { getCookieByName, getSessionApiKeySync } from "@/lib/cookies";
import { resolveLocale } from "@/lib/resolvedLocale";
import { type SupportedLanguage } from "@/lib/shared";
import { TRole } from "@/actions/types";
import { localizedPath } from "@/lib/localePath";
import { useState } from "react";
import { AUTH_SESSION_CHANGED } from "@/lib/authSession";

export default function ProtectedContent() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeLocale = resolveLocale(params.locale as string | undefined);

  const [locale, setLocale] = useState<SupportedLanguage>(routeLocale);
  const [session, setSession] = useState<string | undefined>(() =>
    typeof document !== "undefined" ? getSessionApiKeySync() : undefined,
  );
  const [role, setRole] = useState<TRole | undefined>();

  useLayoutEffect(() => {
    setSession(getSessionApiKeySync());
    setLocale(resolveLocale(params.locale as string | undefined));
    void (async () => {
      const r = await getCookieByName("role");
      setRole(r as TRole);
    })();
  }, [params.locale, location.pathname, location.key]);

  useEffect(() => {
    const sync = () => {
      setSession(getSessionApiKeySync());
      void (async () => {
        const r = await getCookieByName("role");
        setRole(r as TRole);
      })();
    };
    window.addEventListener(AUTH_SESSION_CHANGED, sync);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED, sync);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ["myProfile", "header"],
    queryFn: () => getMyProfile(),
  });

  useEffect(() => {
    if (profile?.status === "error") {
      navigate(localizedPath(locale, "/auth/logout"), { replace: true });
    }
  }, [profile, navigate, locale]);

  return (
    <ProfileContent
      profile={profile?.status === "success" ? profile.metaData : undefined}
      unreadCounter={{
        status: "success",
        metaData: {
          unread_count: 0,
        },
        message: "",
      }}
      notifications={{
        status: "success",
        metaData: [],
        message: "",
      }}
      unreadMessageCount={0}
      locale={locale}
      session={session}
      role={role ?? "Personal"}
    />
  );
}
